import type { CalendarViewId } from '@/constants/calendar/views'
import { addCalendarDays, startOfWeekMonday } from '@/utils/calendar/date'
import { getMonthNames } from '@/utils/calendar/month-names'
import { computed, ref, type MaybeRefOrGetter, type Ref, toValue } from 'vue'
import { useI18n } from 'vue-i18n'

export interface CalendarViewItem {
  id: CalendarViewId
  label: string
}

function shiftMonth(date: Date, delta: number): Date {
  const day = date.getDate()
  const target = new Date(date.getFullYear(), date.getMonth() + delta, 1)
  const daysInTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  target.setDate(Math.min(day, daysInTargetMonth))
  return target
}

/**
 * Main calendar toolbar state (view + range navigation), aligned with Legal OS prototype.
 */
export function useCalendarMainView(
  selectedDate: Ref<Date>,
  labels: MaybeRefOrGetter<{
    viewDay: string
    viewWeek: string
    viewMonth: string
    viewYear: string
    prevDay: string
    prevWeek: string
    prevMonth: string
    prevYear: string
    nextDay: string
    nextWeek: string
    nextMonth: string
    nextYear: string
  }>
) {
  const { locale } = useI18n()
  const activeView = ref<CalendarViewId>('day')

  const monthNames = computed(() => getMonthNames(locale.value))

  const views = computed<CalendarViewItem[]>(() => [
    { id: 'day', label: toValue(labels).viewDay },
    { id: 'week', label: toValue(labels).viewWeek },
    { id: 'month', label: toValue(labels).viewMonth },
    { id: 'year', label: toValue(labels).viewYear }
  ])

  const weekStart = computed(() => startOfWeekMonday(selectedDate.value))

  const heading = computed<{ date: string; year: string }>(() => {
    const d = selectedDate.value
    const { full, short } = monthNames.value
    if (activeView.value === 'week') {
      const start = weekStart.value
      const end = addCalendarDays(start, 6)
      if (start.getMonth() === end.getMonth()) {
        return {
          date: `${start.getDate()}. – ${end.getDate()}. ${full[start.getMonth()]}`,
          year: `${start.getFullYear()}`
        }
      }
      return {
        date: `${start.getDate()}. ${short[start.getMonth()]} – ${end.getDate()}. ${short[end.getMonth()]}`,
        year: `${start.getFullYear()}`
      }
    }
    if (activeView.value === 'month') {
      return {
        date: full[d.getMonth()]!,
        year: `${d.getFullYear()}`
      }
    }
    if (activeView.value === 'year') {
      return { date: `${d.getFullYear()}`, year: '' }
    }
    return {
      date: `${d.getDate()}. ${full[d.getMonth()]}`,
      year: `${d.getFullYear()}`
    }
  })

  const prevLabel = computed(() => {
    if (activeView.value === 'week') return toValue(labels).prevWeek
    if (activeView.value === 'month') return toValue(labels).prevMonth
    if (activeView.value === 'year') return toValue(labels).prevYear
    return toValue(labels).prevDay
  })

  const nextLabel = computed(() => {
    if (activeView.value === 'week') return toValue(labels).nextWeek
    if (activeView.value === 'month') return toValue(labels).nextMonth
    if (activeView.value === 'year') return toValue(labels).nextYear
    return toValue(labels).nextDay
  })

  const setView = (id: CalendarViewId): void => {
    activeView.value = id
  }

  const goPrev = (): void => {
    const d = new Date(selectedDate.value)
    if (activeView.value === 'day') d.setDate(d.getDate() - 1)
    else if (activeView.value === 'week') d.setDate(d.getDate() - 7)
    else if (activeView.value === 'month') selectedDate.value = shiftMonth(d, -1)
    else if (activeView.value === 'year') d.setFullYear(d.getFullYear() - 1)
    if (activeView.value !== 'month') selectedDate.value = d
  }

  const goNext = (): void => {
    const d = new Date(selectedDate.value)
    if (activeView.value === 'day') d.setDate(d.getDate() + 1)
    else if (activeView.value === 'week') d.setDate(d.getDate() + 7)
    else if (activeView.value === 'month') selectedDate.value = shiftMonth(d, 1)
    else if (activeView.value === 'year') d.setFullYear(d.getFullYear() + 1)
    if (activeView.value !== 'month') selectedDate.value = d
  }

  return {
    activeView,
    views,
    heading,
    prevLabel,
    nextLabel,
    setView,
    goPrev,
    goNext
  }
}
